import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiClock, FiChevronUp, FiChevronDown, FiX } from "react-icons/fi";
import usePopoverPosition from "../../../hooks/usePopoverPosition";
// [R22] Minuty spoza kwadransa zostają; strzałki idą po kwadransach; „Wpisz godzinę” daje każdą minutę.
import WpisGodziny, { KWADRANSE, minutaWGore, minutaWDol, najblizszyKwadrans } from "./WpisGodziny";
import styles from "./DatePicker.module.css";
import { useWarstwaNadModalem } from "../../SideDrawer/warstwaNadModalem";

const MINUTE_OPTIONS = KWADRANSE;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

function pad(n) { return String(n).padStart(2, "0"); }

function parseTime(val) {
    if (!val) return { hour: 8, minute: 0, hasValue: false };
    const parts = val.split(":");
    if (parts.length < 2) return { hour: 8, minute: 0, hasValue: false };
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return { hour: 8, minute: 0, hasValue: false };
    // Wczytana minuta zostaje co do minuty (07:50 to 07:50, nie 07:45) — patrz WpisGodziny.js.
    return { hour: Math.min(23, Math.max(0, h)), minute: Math.min(59, Math.max(0, m)), hasValue: true };
}

export default function TimePicker({
    value,
    onChange,
    placeholder = "Wybierz godzinę",
    disabled = false,
    clearable = false,
    className = "",
    /* `nadModalem` — wybor warstwy kalendarza; opis reguły stoi przy DatePicker.js.
       Ten sam arkusz, ten sam defekt: pole w szufladzie nad oknem modalnym musi podniesc
       swoj popover na --z-drawer-over-modal-dropdown, bo --z-datepicker (10001) jest ponizej
       panelu szuflady (10011). Bez propsa decyduje kontekst WarstwaNadModalem (false na zwykłej stronie). */
    nadModalem: nadModalemJawny,
    /* Nazwa pola dla czytnika ekranu — reguła jak w DatePicker.ariaLabel (wyzwalacz to <button>, którego
       treścią jest sama godzina; bez etykiety nie wiadomo, CZEGO to godzina). */
    ariaLabel = null,
}) {
    const nadModalem = useWarstwaNadModalem(nadModalemJawny);
    const parsed = parseTime(value);
    const [open, setOpen] = useState(false);
    const [hour, setHour] = useState(parsed.hour);
    const [minute, setMinute] = useState(parsed.minute);

    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    // Popover stoi PRZY polu (pomiar przed malowaniem) — patrz hooks/usePopoverPosition.
    const { style: popStyle } = usePopoverPosition(open, triggerRef, dropdownRef, { minHeight: 220, fit: true });

    // Sync internal state when value changes externally
    useEffect(() => {
        const p = parseTime(value);
        setHour(p.hour);
        setMinute(p.minute);
    }, [value]);

    const emitChange = useCallback((h, m) => {
        onChange(`${pad(h)}:${pad(m)}`);
    }, [onChange]);

    const toggle = () => { if (!disabled) setOpen(p => !p); };
    const close = () => setOpen(false);

    const selectHour = (h) => {
        setHour(h);
        emitChange(h, minute);
    };

    const selectMinute = (m) => {
        setMinute(m);
        emitChange(hour, m);
    };

    const hourUp = (e) => { e.stopPropagation(); selectHour((hour + 1) % 24); };
    const hourDown = (e) => { e.stopPropagation(); selectHour((hour - 1 + 24) % 24); };
    const minuteUp = (e) => { e.stopPropagation(); selectMinute(minutaWGore(minute)); };
    const minuteDown = (e) => { e.stopPropagation(); selectMinute(minutaWDol(minute)); };

    // [R22] Pełna godzina z klawiatury (gg:mm) — każda minuta, jak w natywnej kontrolce.
    const wpisanaGodzina = (h, m) => {
        setHour(h);
        setMinute(m);
        emitChange(h, m);
    };

    const klawiszOkna = (e) => {
        if (e.key === "Escape") { e.stopPropagation(); close(); }
    };

    const goNow = (e) => {
        e.stopPropagation();
        const now = new Date();
        const h = now.getHours();
        const m = najblizszyKwadrans(now.getMinutes());
        setHour(h);
        setMinute(m);
        emitChange(h, m);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange("");
        close();
    };

    const displayText = value ? `${pad(hour)}:${pad(minute)}` : "";

    const dropdown = open ? createPortal(
        <>
            <div
                className={`${styles.overlay} ${nadModalem ? styles.overlayNadModalem : ""}`}
                onClick={close}
            />
            <div
                className={`${styles.dropdown} ${styles.timeOnlyDropdown} ${nadModalem ? styles.dropdownNadModalem : ""}`}
                ref={dropdownRef}
                style={popStyle}
                onClick={e => e.stopPropagation()}
                onKeyDown={klawiszOkna}
            >
                {/* Spinner row */}
                <div className={styles.timeLabel}>Godzina</div>
                <div className={styles.timeRow}>
                    {/* Hour spinner */}
                    <div className={styles.timeCol}>
                        <div className={styles.timeColLabel}>Godz.</div>
                        <div className={styles.timeScroll}>
                            <button className={styles.timeSpinBtn} type="button" onClick={hourUp}><FiChevronUp /></button>
                            <div className={styles.timeValue}>{pad(hour)}</div>
                            <button className={styles.timeSpinBtn} type="button" onClick={hourDown}><FiChevronDown /></button>
                        </div>
                    </div>

                    <span className={styles.timeSep}>:</span>

                    {/* Minute spinner */}
                    <div className={styles.timeCol}>
                        <div className={styles.timeColLabel}>Min.</div>
                        <div className={styles.timeScroll}>
                            <button className={styles.timeSpinBtn} type="button" onClick={minuteUp}><FiChevronUp /></button>
                            <div className={styles.timeValue}>{pad(minute)}</div>
                            <button className={styles.timeSpinBtn} type="button" onClick={minuteDown}><FiChevronDown /></button>
                        </div>
                    </div>
                </div>

                {/* [R22] Wpis godziny z klawiatury — każda minuta; Enter zapisuje i zamyka */}
                <div className={styles.wpisGodzinyWiersz}>
                    <WpisGodziny
                        hour={hour}
                        minute={minute}
                        maWartosc={!!value}
                        onWpisanaGodzina={wpisanaGodzina}
                        onZatwierdz={close}
                    />
                </div>

                {/* Quick hour grid */}
                <div className={styles.hourGrid}>
                    {HOUR_OPTIONS.map(h => (
                        <button
                            key={h}
                            type="button"
                            className={`${styles.hourBtn} ${h === hour ? styles.hourBtnActive : ""}`}
                            onClick={() => selectHour(h)}
                        >
                            {pad(h)}
                        </button>
                    ))}
                </div>

                {/* Quick minute grid */}
                <div className={styles.minuteGrid}>
                    {MINUTE_OPTIONS.map(m => (
                        <button
                            key={m}
                            type="button"
                            className={`${styles.minuteBtn} ${m === minute ? styles.minuteBtnActive : ""}`}
                            onClick={() => selectMinute(m)}
                        >
                            :{pad(m)}
                        </button>
                    ))}
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button className={styles.todayBtn} type="button" onClick={goNow}>
                        Teraz
                    </button>
                    {clearable && value && (
                        <button className={styles.clearBtn} type="button" onClick={handleClear}>
                            Wyczyść
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.zapiszBtn}
                        onClick={(e) => { e.stopPropagation(); emitChange(hour, minute); close(); }}
                    >
                        Zapisz
                    </button>
                </div>
            </div>
        </>,
        document.body
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.trigger} ${open ? styles.triggerOpen : ""} ${className}`}
                onClick={toggle}
                disabled={disabled}
                aria-label={ariaLabel ? `${ariaLabel}: ${displayText || placeholder}` : undefined}
            >
                <span className={styles.triggerIcon}><FiClock /></span>
                <span className={`${styles.triggerText} ${!value ? styles.triggerPlaceholder : ""}`}>
                    {displayText || placeholder}
                </span>
                {clearable && value && (
                    <span className={styles.triggerClear} onClick={handleClear} role="button" tabIndex={-1}>
                        <FiX />
                    </span>
                )}
            </button>
            {dropdown}
        </>
    );
}
